/**
 * Authentication Service
 * Handles session-based authentication with JWT fallback
 */

import { API_BASE_URL } from '../config';

// Storage keys
const SESSION_ID_KEY = 'session_id';
const USER_ID_KEY = 'user_id';
const AUTH_USER_KEY = 'auth_user';

// Legacy key for backwards compatibility
const LEGACY_TOKEN_KEY = 'jwt_token';

/**
 * Store session data in localStorage
 */
export const setSession = (sessionId, userId, user) => {
  if (sessionId) {
    localStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  if (userId) {
    localStorage.setItem(USER_ID_KEY, userId);
  }
  if (user) {
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  }
};

/**
 * Get session ID from localStorage
 */
export const getSessionId = () => {
  return localStorage.getItem(SESSION_ID_KEY);
};

/**
 * Get user ID from localStorage
 */
export const getUserId = () => {
  return localStorage.getItem(USER_ID_KEY);
};

/**
 * Get stored user from localStorage
 */
export const getStoredUser = () => {
  try {
    const userStr = localStorage.getItem(AUTH_USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    return null;
  }
};

/**
 * Clear all session data
 */
export const clearSession = () => {
  localStorage.removeItem(SESSION_ID_KEY);
  localStorage.removeItem(USER_ID_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  localStorage.removeItem(LEGACY_TOKEN_KEY);
};

/**
 * Check if user has a session
 */
export const hasSession = () => {
  return !!getSessionId();
};

// Legacy support
export const setToken = (token) => {
  if (token) {
    localStorage.setItem(LEGACY_TOKEN_KEY, token);
  }
};

export const getToken = () => {
  return localStorage.getItem(LEGACY_TOKEN_KEY);
};

export const removeToken = clearSession;

export const hasToken = () => {
  return hasSession() || !!getToken();
};

/**
 * Exchange token for session (new API v1)
 * @param {string} token - Short-lived token from URL
 * @param {string} region - Region (US or EU)
 * @returns {Promise<{success: boolean, session: object|null, error: string|null}>}
 */
export const exchangeToken = async (token, region = 'US') => {
  try {
    const url = `${API_BASE_URL}/api/v1/auth/exchange`;
    console.log('=== EXCHANGE TOKEN DEBUG ===');
    console.log('API_BASE_URL:', API_BASE_URL);
    console.log('Full URL:', url);
    console.log('Region:', region);
    console.log('===========================');

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Important for cookies
      body: JSON.stringify({ token, region }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      return {
        success: false,
        session: null,
        error: data.message || data.error?.message || 'Token exchange failed',
      };
    }

    // Store session data
    const sessionData = data.data;
    setSession(sessionData.session_id, sessionData.user_id, {
      id: sessionData.user_id,
      subscriptions: sessionData.subscriptions,
    });

    return {
      success: true,
      session: sessionData,
      error: null,
    };
  } catch (error) {
    console.error('Token exchange error:', error);
    return {
      success: false,
      session: null,
      error: error.message || 'Network error during token exchange',
    };
  }
};

/**
 * Verify current session (new API v1)
 * @returns {Promise<{valid: boolean, user: object|null, error: string|null}>}
 */
export const verifySession = async () => {
  try {
    const sessionId = getSessionId();

    const url = `${API_BASE_URL}/api/v1/auth/verify`;

    const headers = {
      'Content-Type': 'application/json',
    };

    // Include session in header if we have it (backup to cookie)
    if (sessionId) {
      headers['Authorization'] = `Session ${sessionId}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include', // Important for cookies
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      // Session invalid - clear storage
      if (data.error?.details?.clear_storage) {
        clearSession();
      }
      return {
        valid: false,
        user: null,
        error: data.message || 'Session verification failed',
      };
    }

    const userData = data.data;
    return {
      valid: true,
      user: {
        id: userData.user_id,
        subscriptions: userData.subscriptions,
        expires_at: userData.expires_at,
        issued_at: userData.issued_at,
      },
      error: null,
    };
  } catch (error) {
    console.error('Session verification error:', error);
    return {
      valid: false,
      user: null,
      error: error.message || 'Network error during session verification',
    };
  }
};

/**
 * Logout current session
 */
export const logout = async () => {
  try {
    const sessionId = getSessionId();

    const headers = {
      'Content-Type': 'application/json',
    };

    if (sessionId) {
      headers['Authorization'] = `Session ${sessionId}`;
    }

    await fetch(`${API_BASE_URL}/api/v1/auth/logout`, {
      method: 'POST',
      headers,
      credentials: 'include',
    });
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    clearSession();
  }
};

/**
 * Validate JWT token with backend (legacy support)
 * @param {string} token - JWT token to validate
 * @returns {Promise<{valid: boolean, user: object|null, error: string|null}>}
 */
export const validateToken = async (token) => {
  // First try the new exchange endpoint
  const exchangeResult = await exchangeToken(token);

  if (exchangeResult.success) {
    return {
      valid: true,
      user: {
        id: exchangeResult.session.user_id,
        subscriptions: exchangeResult.session.subscriptions,
      },
      error: null,
    };
  }

  // Fall back to legacy validation
  try {
    const url = `${API_BASE_URL}/api/auth/validate`;
    console.log('=== VALIDATE TOKEN (LEGACY) DEBUG ===');
    console.log('API_BASE_URL:', API_BASE_URL);
    console.log('Full URL:', url);
    console.log('===========================');

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        valid: false,
        user: null,
        error: errorData.detail || 'Token validation failed',
      };
    }

    const data = await response.json();

    // Store token for legacy support
    setToken(token);

    return {
      valid: data.valid,
      user: data,
      error: null,
    };
  } catch (error) {
    console.error('Token validation error:', error);
    return {
      valid: false,
      user: null,
      error: error.message || 'Network error during token validation',
    };
  }
};

/**
 * Get current user info from session
 * @returns {Promise<object|null>}
 */
export const getCurrentUser = async () => {
  const result = await verifySession();
  if (result.valid) {
    return result.user;
  }

  // Fall back to legacy token validation
  const token = getToken();
  if (token) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        clearSession();
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  }

  return null;
};

/**
 * Extract token from URL query parameter
 * @param {string} search - window.location.search
 * @returns {string|null}
 */
export const extractTokenFromUrl = (search) => {
  const params = new URLSearchParams(search);
  return params.get('token');
};

/**
 * Extract region from URL query parameter
 * @param {string} search - window.location.search
 * @returns {string}
 */
export const extractRegionFromUrl = (search) => {
  const params = new URLSearchParams(search);
  return params.get('region') || 'US';
};

/**
 * Clean URL after extracting token (remove sensitive params from URL)
 */
export const cleanUrlAfterTokenExtraction = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('token');
  url.searchParams.delete('region');
  url.searchParams.delete('return-url');
  window.history.replaceState({}, document.title, url.pathname + url.search);
};

/**
 * Check if user has a specific product
 * @param {object} user - User object with subscriptions
 * @param {string} product - Product code (e.g., 'INCV', 'INC')
 * @returns {boolean}
 */
export const hasProduct = (user, product) => {
  if (!user?.subscriptions) return false;

  const subs = user.subscriptions;

  // Handle dict format: {"FINMC": ["INCV", "INC"]}
  if (typeof subs === 'object' && !Array.isArray(subs)) {
    for (const key of Object.keys(subs)) {
      const value = subs[key];
      if (Array.isArray(value) && value.includes(product)) {
        return true;
      }
      if (value === product || value === true) {
        return true;
      }
    }
  }

  // Handle array format: ["INCV", "INC"]
  if (Array.isArray(subs)) {
    return subs.includes(product);
  }

  return false;
};
